import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TableProvider, usePrevious } from './Hooks';
import { Pagination } from './Pagination';
import { TableContent } from './TableContent';
import { Loader } from './Loader';
import { filteratrion } from './Utils';

const columnsProps = {
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType])
		.isRequired,
	dataIndex: function (props, propName, componentName) {
		if (typeof props[propName] !== 'string' && !('columns' in props)) {
			return new Error(
				'Invalid prop `' +
					propName +
					'` is marked as required in ' +
					' `' +
					componentName +
					'`, expected `string`.'
			);
		}
	},
	render: PropTypes.func,
	cellAttr: PropTypes.func,
	mountCellWhen: PropTypes.func,
	sortable: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
	sortDirection: PropTypes.arrayOf(PropTypes.oneOf(['asc', 'desc'])),
	sortOrderDefault: PropTypes.oneOf(['asc', 'desc']),
	filters: PropTypes.arrayOf(
		PropTypes.shape({ label: PropTypes.string, like: PropTypes.string })
	),
	filterMultiple: PropTypes.bool,
	filterSearch: PropTypes.exact({
		label: PropTypes.string,
		like: PropTypes.string,
	}),
};
const TableProps = {
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			...columnsProps,
			columns: PropTypes.arrayOf(
				PropTypes.shape({
					...columnsProps,
					columns: PropTypes.arrayOf(
						PropTypes.shape({
							...columnsProps,
							columns: PropTypes.arrayOf(PropTypes.shape(columnsProps)),
						})
					),
				})
			),
		})
	).isRequired,
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	pagination: PropTypes.shape({
		pageSize: PropTypes.number,
		totalRows: PropTypes.number,
		currentPage: PropTypes.number,
	}),
	collapsible: PropTypes.shape({
		render: PropTypes.func,
		collapseWhen: PropTypes.func,
		collapseWhenClickRow: PropTypes.bool,
		accordion: PropTypes.bool,
		transition: PropTypes.shape({
			duration: PropTypes.number,
			timingFunction: PropTypes.string,
		}),
	}),
	className: PropTypes.string,
	asynchronous: PropTypes.bool,
	onChange: PropTypes.func,
	children: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.arrayOf(PropTypes.element),
	]),
};

const TableDefault = {
	pagination: {},
	collapsible: {},
};

export const Table = ({
	data,
	columns,
	pagination: { pageSize = 0, totalRows, currentPage = 1 },
	collapsible,
	className,
	asynchronous, // ill make loading inside when the data is change then loading is remove
	onChange,
	children,
}) => {
	const [state, setState] = useState({
		sort: {
			orderBy: null,
			order: null,
		},
		filters: [],
		page: currentPage,
		pageSize,
		loading: false,
		mounted: false,
	});
	const sortOrderDefault =
		columns.find((column) =>
			['asc', 'desc'].includes(column.sortOrderDefault)
		) || null;
	const previousSortOrderDefault = usePrevious(sortOrderDefault);
	const previousCurrentPage = usePrevious(currentPage);
	const previousPageSize = usePrevious(pageSize);
	useEffect(() => {
		const update = {};
		const columnsHasChange = sortOrderDefault !== previousSortOrderDefault;
		const currentPageHasChange = currentPage !== previousCurrentPage;
		const pageSizeHasChange = pageSize !== previousPageSize;
		const hasChange =
			columnsHasChange || currentPageHasChange || pageSizeHasChange;
		if (columnsHasChange) {
			update.sort = {
				orderBy: sortOrderDefault && sortOrderDefault.dataIndex,
				order: sortOrderDefault && sortOrderDefault.sortOrderDefault,
			};
		}
		if (currentPageHasChange) {
			update.page = currentPage;
		}
		if (pageSizeHasChange) {
			update.page = 1;
			update.pageSize = pageSize;
		}
		hasChange &&
			setState((prev) => ({
				...prev,
				...update,
				mounted: true,
			}));
	}, [
		sortOrderDefault,
		currentPage,
		pageSize,
		previousSortOrderDefault,
		previousCurrentPage,
		previousPageSize,
	]);
	useEffect(() => {
		if (asynchronous) {
			setState((prev) => ({
				...prev,
				loading: false,
			}));
		}
	}, [asynchronous, data]);
	useEffect(() => {
		if (!state.mounted) return;
		onChange &&
			onChange({
				sort: { ...state.sort },
				filters: state.filters.map((each) => ({ ...each })),
				pagination: { currentPage: state.page, pageSize: state.pageSize },
			});
		asynchronous &&
			setState((prev) => ({
				...prev,
				loading: true,
			}));
	}, [
		state.mounted,
		state.sort,
		state.filters,
		state.page,
		state.pageSize,
		onChange,
		asynchronous,
	]);

	const provide = useMemo(() => {
		const source =
			!asynchronous && state.filters.length
				? filteratrion(state.filters, data)
				: data;
		return {
			state,
			setState,
			data: source,
			columns,
			pageSize,
			rowCount: totalRows || source.length,
			collapsible,
			asynchronous,
		};
	}, [state, data, columns, pageSize, totalRows, collapsible, asynchronous]);
	return (
		<TableProvider value={provide}>
			{children || (
				<div className='reatable'>
					<div className='reatable-content'>
						<TableContent className={className} />
					</div>
					<div className='reatable-footer'>
						<Pagination />
					</div>
					<Loader />
				</div>
			)}
		</TableProvider>
	);
};

Table.propTypes = TableProps;
Table.defaultProps = TableDefault;
